import React, { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Form } from "react-bootstrap";
import { DataGridColumnConfig, DataGridColumnSelectValue } from "./types";
import { getColumnFilterInputType } from "./utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { isEmpty } from "lodash";
import useMediaQuery from "components/utils/useMediaQuery";

export const DataGridFilterInput = forwardRef<
    HTMLElement,
    {
        name?: string;
        className?: string;
        upperCaseValue?: boolean;
        showPlaceholder?: boolean;
        placeHolder?: string;
        value?: string;
        column?: DataGridColumnConfig;
        inputType?: "text" | "date" | "select";
        selectValues?: DataGridColumnSelectValue[];
        onChange: (value: string, key?: string) => void;
        onKeyDown?: (columnKey: string, event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    }
>(
    (
        {
            className,
            upperCaseValue = true,
            column,
            value: valueProp = "",
            name,
            inputType: inputTypeProp,
            showPlaceholder = false,
            placeHolder: placeHolderProp,
            selectValues,
            onChange,
            onKeyDown,
        },
        ref
    ) => {
        const inputType = inputTypeProp ?? getColumnFilterInputType(column);
        const [value, setValue] = useState(valueProp);
        const [previousValue, setPreviousValue] = useState(valueProp);
        const inputRef = useRef<HTMLInputElement | HTMLButtonElement | null>(null);
        const isMobile = useMediaQuery("(max-width: 768px)");
        const placeHolder = placeHolderProp ?? (isMobile ? "Please select" : "Search");

        useImperativeHandle(ref, () => inputRef.current!, []);

        if (previousValue !== valueProp) {
            setValue(valueProp);
            setPreviousValue(valueProp);
        }

        const handleTextChange = (value: string) => {
            setValue(value);
            onChange(value, column?.key);
        };

        const handleTextKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onKeyDown?.(column?.key ?? "", event);
        };

        const onClearClick = () => {
            inputRef.current?.focus();
            setValue("");
            onChange("", column?.key ?? "");
        };

        if (column?.filter !== "true" && !["select"].includes(inputType)) return null;

        if (inputType === "select") {
            return (
                <Form.Select
                    ref={inputRef as any}
                    onFocus={() => inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })}
                    className={cn("data-grid-filter-select", { "border-0": !isMobile })}
                    style={{ backgroundImage: "none" }}
                    aria-label={column?.name ?? ""}
                    value={value}
                    onChange={(e) => handleTextChange(e.target.value)}
                    id={`${column?.key ?? name ?? "select"}-filter`}
                >
                    {showPlaceholder && (
                        <option selected value="" className="text-capitalize">
                            {placeHolder}
                        </option>
                    )}
                    {(selectValues ?? column?.selectValues ?? []).map((item, idx) => (
                        <option key={idx} value={item.value}>
                            {upperCaseValue ? `${item.title}`.toUpperCase() : item.title}
                        </option>
                    ))}
                </Form.Select>
            );
        }

        return (
            <div style={{ minWidth: "14ch" }} className={cn("position-relative data-grid-filter-wrapper", className)}>
                <FontAwesomeIcon
                    className="position-absolute top-50 fs-7"
                    style={{ left: 12, opacity: 0.7, transform: "translateY(-60%)" }}
                    icon={["fal", "magnifying-glass"]}
                />
                <Form.Control
                    ref={inputRef as any}
                    style={{ paddingInline: 36 }}
                    className={cn("text-truncate", { "border-0": !isMobile })}
                    onFocus={() => inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })}
                    aria-label={column?.name ?? name}
                    type={inputType}
                    placeholder={placeHolderProp ?? "Search..."}
                    value={value}
                    onChange={(e) => handleTextChange(e.target.value)}
                    onKeyDown={handleTextKeyDown}
                    name={name}
                    id={`${column?.key ?? name ?? "form-control"}-filter`}
                />
                {!isEmpty(value) && (
                    <button
                        className="position-absolute top-50 translate-middle-y border-0 p-0 bg-transparent"
                        style={{ right: 12 }}
                        onClick={onClearClick}
                        aria-label="Clear filter"
                    >
                        <FontAwesomeIcon icon={"circle-xmark"} className="fs-7" />
                    </button>
                )}
            </div>
        );
    }
);

DataGridFilterInput.displayName = "DataGridFilterInput";
